import React, { useState, useEffect } from "react";
import { ArrowRight, Trophy, CalendarDays } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Link } from "react-router-dom";

// Reusable CountdownTimer component
function CountdownTimer({ targetDate, status }) {
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  
  // If status is explicitly provided, use it
  if (status === "ended") {
    return <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-500/50 px-3 py-1 font-medium rounded-md">Competition Ended</Badge>;
  }
  
  // Force show "Competition Ended" for any date that's passed
  if (targetDate < currentTime) {
    return <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-500/50 px-3 py-1 font-medium rounded-md">Competition Ended</Badge>;
  }
  
  // For actual live competitions
  const competitionEnd = new Date(targetDate.getTime() + (24 * 60 * 60 * 1000));
  if (currentTime >= targetDate && currentTime <= competitionEnd) {
    return <Badge variant="destructive" className="text-sm font-medium px-3 py-1">Live Now!</Badge>;
  }
  
  // Calculate countdown for future competitions
  const timeLeft = targetDate - currentTime;
  const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeLeft / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((timeLeft / (1000 * 60)) % 60);
  const seconds = Math.floor((timeLeft / 1000) % 60);
  
  // For TBD dates
  if (status === "tbd") {
    return (
      <div className="flex flex-col items-center gap-1">
        <Badge variant="outline" className="bg-transparent border-yellow-500/20 text-yellow-600 shadow-none">Starts in</Badge>
        <div className="text-sm font-medium text-yellow-600">TBD</div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col items-center gap-1">
      <Badge variant="outline" className="bg-transparent border-yellow-500/20 text-yellow-600 shadow-none">Starts in</Badge>
      <div className="grid grid-flow-col gap-1 text-center auto-cols-max">
        <div className="flex flex-col p-1 bg-transparent rounded-md shadow-none">
          <span className="font-mono text-sm font-bold">{days}</span>
          <span className="text-xs text-muted-foreground">days</span>
        </div>
        <div className="flex flex-col p-1 bg-transparent rounded-md shadow-none">
          <span className="font-mono text-sm font-bold">{hours}</span>
          <span className="text-xs text-muted-foreground">hrs</span>
        </div>
        <div className="flex flex-col p-1 bg-transparent rounded-md shadow-none">
          <span className="font-mono text-sm font-bold">{minutes}</span>
          <span className="text-xs text-muted-foreground">min</span>
        </div>
        <div className="flex flex-col p-1 bg-transparent rounded-md shadow-none">
          <span className="font-mono text-sm font-bold">{seconds}</span>
          <span className="text-xs text-muted-foreground">sec</span>
        </div>
      </div>
    </div>
  );
}

// Image component with fallback
function CompetitionImage({ src, alt, fallbackSrc }) {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  
  const handleError = () => {
    if (!hasError && fallbackSrc) {
      setImgSrc(fallbackSrc);
      setHasError(true);
    }
  };
  
  return (
    <img 
      src={imgSrc} 
      alt={alt} 
      onError={handleError}
      className="w-full h-full object-cover object-top"
    />
  );
}

// Active competitions data
export const activeCompetitions = [
  {
    id: 1,
    title: "Ariana Grande Look Alike",
    description: "Show off your best Ariana Grande impression and style in this exciting competition. Submit before 5PM EST, then vote until 6PM EST.",
    prize: "1st: $100, 2nd: $75, 3rd: $25",
    date: "April 13, 2025 (1-hour event)",
    image: "/images/Ariana_Grande.jpg",
    targetDate: new Date("2025-04-13T16:00:00-05:00"), // 4PM EST, before submission ends
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 2,
    title: "Bad Bunny Look Alike",
    description: "Show off your best Bad Bunny impression and style in this exciting competition.",
    prize: "$500",
    date: "TBD",
    image: "/images/BadBunny.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 3,
    title: "Aaron Taylor-Johnson Look Alike",
    description: "Create your best Aaron Taylor-Johnson impression in this exciting competition.",
    prize: "$500",
    date: "TBD",
    image: "/images/aarontaylorjohnson.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 4,
    title: "Sabrina Carpenter Look Alike",
    description: "Show off your best Sabrina Carpenter impression and style in this competition.",
    prize: "$500",
    date: "TBD",
    image: "/images/SabrinaCarpenter.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 5,
    title: "MrBeast Look Alike",
    description: "Create your best MrBeast impression and win exciting prizes.",
    prize: "$500",
    date: "TBD",
    image: "/images/MrBeast.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 6,
    title: "Dream Look Alike",
    description: "Show off your best Dream impression in this exciting competition.",
    prize: "$500",
    date: "TBD",
    image: "/images/Dream.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 7,
    title: "Pokimane Look Alike",
    description: "Create your best Pokimane impression and style to win prizes.",
    prize: "$500",
    date: "TBD",
    image: "/images/pokimane.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 8,
    title: "Lorde Look Alike",
    description: "Show off your best Lorde impression and style in this competition.",
    prize: "$500",
    date: "TBD",
    image: "/images/Lorde.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 9,
    title: "Addison Rae Look Alike",
    description: "Create your best Addison Rae impression to win prizes.",
    prize: "$500",
    date: "TBD",
    image: "/images/AddisonRae.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 10,
    title: "Billie Eilish Look Alike",
    description: "Show off your best Billie Eilish impression and style in this competition.",
    prize: "$500",
    date: "TBD",
    image: "/images/billieeilish.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 11,
    title: "Doja Cat Look Alike",
    description: "Create your best Doja Cat impression and style to win prizes.",
    prize: "$500",
    date: "TBD",
    image: "/images/dojacat.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 12,
    title: "Emoji Face-Off",
    description: "Create your best emoji face expressions in this fun competition.",
    prize: "$500",
    date: "TBD",
    image: "/images/emojiface.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 13,
    title: "NPC Acting",
    description: "Show off your best NPC acting skills in this exciting competition.",
    prize: "$500",
    date: "TBD",
    image: "/images/npc.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 14,
    title: "Pigeon Impersonation",
    description: "Create your best pigeon impression in this fun and unique competition.",
    prize: "$500",
    date: "TBD",
    image: "/images/PigeonImpersonation.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  },
  {
    id: 15,
    title: "Playboi Carti Look Alike",
    description: "Show off your best Playboi Carti impression and style in this exciting competition.",
    prize: "$500",
    date: "TBD",
    image: "/images/playboicarti.jpg",
    targetDate: new Date("2025-06-01T16:00:00-05:00"),
    category: "Video entry",
    status: "upcoming"
  }
];

export default function CompetitionsSection() {
  // Sort competitions to show ended competitions last
  const sortedCompetitions = [...activeCompetitions].sort((a, b) => {
    // Move ended competitions to the end
    const aIsEnded = a.status === "ended" || a.targetDate < new Date();
    const bIsEnded = b.status === "ended" || b.targetDate < new Date();
    
    if (aIsEnded && !bIsEnded) return 1;
    if (!aIsEnded && bIsEnded) return -1;
    
    return a.id - b.id;
  });
  
  // Find Emoji Face-Off competition
  const emojiIndex = sortedCompetitions.findIndex(comp => comp.title === "Emoji Face-Off");
  
  // If found, move it to the 5th position
  if (emojiIndex !== -1) {
    const emojiComp = sortedCompetitions.splice(emojiIndex, 1)[0];
    sortedCompetitions.splice(4, 0, emojiComp);
  }
  
  // Apply TBD status to all competitions with TBD date
  sortedCompetitions.forEach(comp => {
    if (comp.date === "TBD") {
      comp.status = "tbd";
    }
  });
  
  return (
    <section id="competitions" className="bg-muted/50 py-24">
      <div className="container space-y-12">
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
            Active Competitions
          </h2>
          <p className="text-muted-foreground max-w-3xl mx-auto">
            Join our exciting competitions and showcase your talents to win amazing prizes.
          </p>
        </div>
        
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3">
          {sortedCompetitions.map(competition => (
            <Card key={competition.id} className="overflow-hidden h-full flex flex-col">
              <div className="h-48 bg-muted flex items-center justify-center relative">
                {competition.image ? (
                  <CompetitionImage 
                    src={competition.image} 
                    alt={competition.title} 
                    fallbackSrc={competition.fallbackImage || "/images/makeupcompetition1.png"}
                  />
                ) : (
                  <div className="text-4xl font-bold text-yellow-500">{competition.category}</div>
                )}
                <Badge 
                  className="absolute top-4 left-4 bg-yellow-500 text-white border-0 shadow-md font-bold px-4 py-2 text-base"
                >
                  {competition.prize}
                </Badge>
              </div>
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex-grow">
                  <Badge 
                    className="bg-yellow-500/20 text-yellow-600 border-0 shadow-none font-medium px-3 py-1 text-sm inline-block mb-1"
                  >
                    {competition.category}
                  </Badge>
                  
                  <h3 className="text-xl font-bold mb-3">{competition.title}</h3>
                  
                  <p className="text-sm text-muted-foreground mb-6">
                    {competition.description}
                  </p>
                </div>
                
                <div className="mt-auto space-y-6">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm">{competition.date}</span>
                  </div>
                  
                  <div className="flex justify-center mb-6">
                    <CountdownTimer targetDate={competition.targetDate} status={competition.status} />
                  </div>
                  
                  <Link to={`/competitions/${competition.id}`} className="block mt-4">
                    <Button variant="default" className="w-full gap-1 bg-yellow-500 hover:bg-yellow-600 text-white">
                      View Details <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="text-center mt-8">
          <Link to="/competitions">
            <Button variant="outline" className="gap-2 border-yellow-500 text-yellow-600 hover:bg-yellow-500/10">
              See All Competitions <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
} 